import type {FC, ReactNode} from 'react';
import clsx from 'clsx';
import Image from 'next/image';

import type {LinkGroup} from '@/modules/finkn/content-blocks/components/group-link-list-selection/GroupLinkList';
import {Container} from '@/modules/foundation/components/container';
import {Link} from '@/modules/foundation/components/link';
import type {KeyedArray} from '@/modules/foundation/shared/types';

export type FooterProps = {
	organizationNumber?: string;
	linkGroups?: KeyedArray<LinkGroup>;
	description?: ReactNode;
};

export const Footer: FC<FooterProps> = ({linkGroups, organizationNumber, description}) => {
	return (
		<Container as="footer" className={clsx('py-8', 'lg:py-12', 'bg-primary-5', 'text-white')}>
			<div>
				<div className={clsx('grid', 'lg:grid-cols-4', 'gap-12')}>
					{linkGroups?.length ? (
						<div className="col-span-full text-sm @container lg:col-span-3">
							<div
								className={clsx('col-span-3', 'grid', '@2xl:grid-cols-3', 'gap-12')}
							>
								{linkGroups.map((group) => {
									const id = group._key;

									return (
										<div key={group._key}>
											{group.title && (
												<h2
													className={clsx(
														'font-medium',
														'mb-3',
														'text-[#B1C1DE]',
														'text-base',
														'font-display',
													)}
													id={id}
												>
													{group.title}
												</h2>
											)}
											<ul {...(group.title && {'aria-labelledby': id})}>
												{group.links.map((link) => (
													<li key={link._key}>
														<Link
															targetBlank={link.targetBlank}
															href={link.href}
															className={clsx(
																'inline-block',
																'py-2',
																'no-underline',
																'text-secondary-70',
																'hover:text-secondary-95',
																'text-[13px]',
																'font-medium',
																'transition-colors',
															)}
														>
															{link.title}
														</Link>
													</li>
												))}
											</ul>
										</div>
									);
								})}
							</div>
						</div>
					) : null}

					<div
						className={clsx(
							'col-span-full',
							'lg:col-span-1',
							'text-[#D4E0F7]',
							'max-lg:text-center',
							'flex',
							'flex-col',
							'max-lg:items-center',
							'max-w-[360px]',
							'mx-auto',
							'lg:-order-1',
						)}
					>
						<Image
							src="/images/logo-light.svg"
							alt=""
							width={270}
							height={35}
							sizes="270px"
							className="mb-4 shrink-0 max-lg:max-w-[220px] lg:mb-8"
						/>

						{description && (
							<div
								className={clsx(
									'mb-4',
									'[&_*]:leading-snug',
									'[&_*]:text-[13px]',
									'[&_*]:lg:text-sm',
								)}
							>
								{description}
							</div>
						)}

						{organizationNumber && (
							<p className={clsx('text-[13px]', 'lg:text-sm')}>
								<span className="font-bold">Org. nr.:</span> {organizationNumber}
							</p>
						)}
					</div>
				</div>
			</div>
		</Container>
	);
};
